// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Shared SVG helpers. Everything here is hand-rolled — no chart library — and
// every layout that computes positions has a positional test in tests/.

export const NS = 'http://www.w3.org/2000/svg';

export function svgEl<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(NS, name);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

/**
 * Create a chart SVG.
 *
 * THE `width:100%` TRAP. An SVG with a viewBox and `width:100%` scales its whole
 * coordinate system to the container — text included. A 13px label inside a
 * 900-wide viewBox rendered in a 1700px container paints at 24.6px; the same
 * chart in a 375px container paints it at 5.4px, well below legibility. Charts
 * here declare a `max-width` equal to their authored width so the rendered type
 * size never strays far from the declared one, and scroll horizontally on
 * narrow screens instead of shrinking.
 */
export function chartSvg(width: number, height: number, title: string): SVGSVGElement {
  const svg = svgEl('svg', {
    viewBox: `0 0 ${width} ${height}`,
    width,
    height,
    class: 'chart',
    role: 'img',
    'aria-label': title,
    preserveAspectRatio: 'xMidYMid meet',
  });
  svg.style.maxWidth = `${width}px`;
  return svg;
}

export function text(
  x: number, y: number, content: string,
  attrs: Record<string, string | number> = {},
): SVGTextElement {
  const t = svgEl('text', { x, y, 'font-size': 13, ...attrs });
  t.textContent = content;
  return t;
}

/** A nice axis tick step for a value range. */
export function tickStep(range: number, target = 6): number {
  if (!(range > 0)) return 1;
  const raw = range / target;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = norm >= 5 ? 10 : norm >= 2 ? 5 : norm >= 1 ? 2 : 1;
  return step * mag;
}

export function ticks(lo: number, hi: number, target = 6): number[] {
  const step = tickStep(hi - lo, target);
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return out;
}

/** Build an SVG path from points, breaking the line at nulls rather than
 *  drawing a straight segment across a gap that has no data in it. */
export function linePath(points: Array<[number, number] | null>): string {
  let d = '';
  let pen = false;
  for (const p of points) {
    if (!p) { pen = false; continue; }
    d += `${pen ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`;
    pen = true;
  }
  return d;
}

/** Add a hoverable data mark: styled tooltip via [data-tip], label via aria. */
export function mark<T extends SVGElement>(el: T, tip: string, label = tip): T {
  el.setAttribute('data-tip', tip);
  el.setAttribute('aria-label', label);
  el.classList.add('mark');
  return el;
}

export function legend(items: Array<{ label: string; colour: string }>): HTMLDivElement {
  const div = document.createElement('div');
  div.className = 'legend';
  for (const it of items) {
    const span = document.createElement('span');
    span.className = 'key';
    const sw = document.createElement('span');
    sw.className = 'swatch';
    sw.style.background = it.colour;
    span.appendChild(sw);
    span.appendChild(document.createTextNode(it.label));
    div.appendChild(span);
  }
  return div;
}
